import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import useCopyDataset from "hooks/useCopyDataset";
import { DatasetOverrideTitleConfig } from "models/dataset-models";
import { InputSwitch } from "primereact/inputswitch";
import React, { useId, useState } from "react";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Tooltip } from "react-tooltip";
import { DATE_FORMATTER_SECONDS } from "utils/constants";
import { fmtDatetime } from "utils/time-utils";
import { I, T } from "services/lang/LanguageService";
import { ZoneId } from "@js-joda/core";
import {
  formatValueWithGivenDecimalPlace,
  numberArrayToBooleanConverter,
} from "../dashboard/data-input-output-table/utils";

export const TitleOverrideAndDescriptionTemplate = ({
  data,
  version,
}: {
  data: DatasetOverrideTitleConfig;
  version?: number;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  useCopyDataset(isHovered, data.title, data.identifier);
  const tooltipId = useId();
  const hasOverride = Boolean(data.overriddenTitle || data.overriddenTitle === " ");
  const label = I(hasOverride ? data.overriddenTitle || "" : data.title || "");
  const unit = data.unit && data.unit.trim() !== "" ? data.unit : "";

  const getStyles = (): React.CSSProperties => {
    const defaultStyles: React.CSSProperties = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "3px",
    };

    const withTextWrapping: React.CSSProperties = {
      whiteSpace: "break-spaces",
      wordBreak: "break-word",
    };

    if (data.overriddenTitle || data.overriddenTitle === " ") {
      if (!version || version === 1) {
        return {
          ...defaultStyles,
          ...withTextWrapping,
        };
      }
      if (version === 2) {
        return {
          ...defaultStyles,
          ...withTextWrapping,
          flexDirection: "column",
          width: "100%",
        };
      }
      if (version === 3) {
        return {
          ...defaultStyles,
          flexDirection: "column",
          width: "100%",
        };
      }
    }
    if (!version || version === 1) {
      return {
        ...defaultStyles,
        ...withTextWrapping,
      };
    }
    if (version === 2) {
      return {
        ...defaultStyles,
        ...withTextWrapping,
        flexDirection: "column",
        flexGrow: "1",
        width: "100%",
      };
    }
    if (version === 3) {
      return {
        ...defaultStyles,
        flexDirection: "column",
        flexGrow: "1",
        width: "100%",
      };
    }
    return defaultStyles;
  };

  return (
    <div style={getStyles()}>
      <Tooltip id={`custom-tooltip-class-${tooltipId}`} place="bottom" className="z-5" />
      <Tooltip id={`custom-target-icon-${tooltipId}`} place="bottom" className="z-5" />

      <div
        data-tooltip-id={`custom-target-icon-${tooltipId}`}
        data-tooltip-content={I(data.description || "")}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      >
        {label}
        {label && unit && ", "}
        {unit}
      </div>
    </div>
  );
};

export const numberTypeTemplate = (
  data: Array<InstantDoubleValue>,
  unit: String,
  decimalPlaces: number | undefined,
  zoneId: ZoneId
) => {
  if (data.length > 0) {
    return (
      <span
        data-tooltip-id="dween-global-tooltip"
        data-tooltip-content={fmtDatetime(data[0].key as string, zoneId, DATE_FORMATTER_SECONDS)}
      >
        {formatValueWithGivenDecimalPlace(data[data.length - 1].value!, unit, decimalPlaces)}
      </span>
    );
  }
  return "-";
};

const confirm = (e: any, onChange: (value: boolean) => void) => {
  easasConfirmDialog({
    icon: "pi pi-exclamation-triangle",
    header: T("action.confirm-action"),
    message: T("confirm-dialog.are-you-sure-you-want-to-change-this-value"),
    acceptClassName: "p-button-success",
    confirmedCallback: () => onChange(e.value),
  });
};

export const BooleanType = ({
  data,
  onChange,
  zoneId,
  hideTooltip,
  tooltip,
}: {
  data: Array<InstantDoubleValue>;
  onChange: ((value: boolean) => void) | undefined;
  zoneId: ZoneId;
  hideTooltip?: boolean;
  tooltip?: string | null;
}) => {
  if (data.length > 0) {
    const getTooltip = () => {
      if (hideTooltip) return null;
      return tooltip || fmtDatetime(data[0].key as string, zoneId, DATE_FORMATTER_SECONDS);
    };
    return (
      <span
        data-tooltip-id="dween-global-tooltip"
        data-tooltip-content={getTooltip()}
        className="flex align-items-center"
      >
        <InputSwitch
          checked={Boolean(numberArrayToBooleanConverter(data))}
          onChange={e => (onChange ? confirm(e, onChange) : null)}
          disabled={!onChange}
        />
      </span>
    );
  }
  return "-";
};
